<template>
  <div class="dashboard-page">
    <AdminPreviewBanner 
      :visible="isAdminPreview && showPreviewBanner" 
      @close="showPreviewBanner = false" 
    />
    <LoadingSpinner :visible="isLoading" />

    <div class="dashboard-title-block" v-show="!isLoading" :style="isAdminPreview && showPreviewBanner ? 'margin-top: 44px;' : ''">
      <h1 class="dashboard-title">非上市公司数据资产入表情况</h1>
    </div>

    <FilterSection
      v-show="!isLoading"
      :apiPrefix="'/api/nlasset'"
      :options="options"
      :filters="pendingFilters"
      :is-loading="isFilterLoading"
      :is-admin-preview="isAdminPreview"
      @filter-change="handleFilterChange"
    />

    <div class="dashboard-container" v-show="!isLoading">
      <ChartRow 
        :filters="filters" 
        :mode="'nls'" 
        :charts="charts"
        :combo-chart-data="comboChartData"
        :is-admin-preview="isAdminPreview"
      />
      <DataTable
        ref="dataTable"
        :filters="filters"
        :tableData="currentPageData"
        :totalRows="totalRows"
        :apiPrefix="'/api/nlasset'"
        :is-loading="isFilterLoading"
        :is-admin-preview="isAdminPreview"
        @page-change="handlePageChange"
      />
    </div>

    <footer class="page-footer" v-show="!isLoading">
      {{ footerText }}
    </footer>

  </div>
</template>

<script>
import FilterSection from '@/components/dashboard/filters/NLFilterSection.vue';
import ChartRow from '@/components/dashboard/NLChartRow.vue';
import DataTable from '@/components/dashboard/NLDataTable.vue';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import AdminPreviewBanner from '@/components/common/AdminPreviewBanner.vue';
import axios from 'axios';

export default {
  name: 'NLSDashboardPage',
  components: {
    FilterSection,
    ChartRow,
    DataTable,
    LoadingSpinner,
    AdminPreviewBanner
  },
  data() {
    return {
      isLoading: true,
      filters: {},
      pendingFilters: {},
      pageSize: 10,
      currentPageData: [],
      totalRows: 0,
      charts: {},
      options: {},
      comboChartData: [],
      footerText: '数据来源：公开信息、高金智库数据资产研究课题组',
      // 数据同步相关
      lastUpdateTime: null,
      isPageVisible: true,
      // 筛选 loading 状态
      isFilterLoading: false,
      isAdminPreview: false,
      showPreviewBanner: true
    };
  },
  methods: {
    async handleFilterChange(newFilters) {
      console.log('【前端 NLSDashboardPage】handleFilterChange 接收到 newFilters:', JSON.stringify(newFilters, null, 2));
      this.filters = { ...newFilters };
      console.log('【前端 NLSDashboardPage】this.filters 更新为:', JSON.stringify(this.filters, null, 2));
      
      // 设置筛选 loading 状态
      this.isFilterLoading = true;
      
      try {
        await this.fetchSummaryData(1);
      } finally {
        // 重置筛选 loading 状态
        this.isFilterLoading = false;
      }
    },
    async handlePageChange(page) {
      await this.fetchSummaryData(page);
    },
    async fetchSummaryData(page) {
      console.log('【前端 NLSDashboardPage】fetchSummaryData 发送的 filters:', JSON.stringify(this.filters, null, 2), '请求页码:', page);
      try {
        const queryParams = this.isAdminPreview ? { adminView: 'true' } : {};
        const res = await axios.post('/api/nlasset/summary', {
          filters: this.filters,
          page,
          pageSize: this.pageSize
        }, { params: queryParams });
        
        this.comboChartData = res.data.cumulativeComboChart || [];
        
        this.charts = res.data.charts || {};
        this.currentPageData = res.data.table.rows || [];
        this.totalRows = res.data.table.total || 0;
        this.options = res.data.options || {};
        this.pendingFilters = { ...this.filters };
        
        // 记录数据更新时间
        this.lastUpdateTime = new Date();

      } catch (err) {
        console.error('❌ 加载 summary 数据失败:', err);
      } finally {
        this.isLoading = false;
        this.$refs.dataTable?.stopLoading?.();
      }
    },
    
    // 页面可见性变化处理
    handleVisibilityChange() {
      this.isPageVisible = !document.hidden;
      if (this.isPageVisible) {
        // 页面重新可见时，刷新数据
        console.log('【前端 NLSDashboardPage】页面重新可见，刷新数据');
        this.fetchSummaryData(1);
      }
    },
    
    // 格式化最后更新时间
    formatLastUpdateTime(time) {
      if (!time) return '';
      const now = new Date();
      const diff = now - time;
      
      if (diff < 60000) { // 1分钟内
        return '刚刚';
      } else if (diff < 3600000) { // 1小时内
        const minutes = Math.floor(diff / 60000);
        return `${minutes}分钟前`;
      } else if (diff < 86400000) { // 1天内
        const hours = Math.floor(diff / 3600000);
        return `${hours}小时前`;
      } else {
        return time.toLocaleString('zh-CN');
      }
    }
  },
  mounted() {
    this.isAdminPreview = this.$route.query.adminView === 'true';
    this.fetchSummaryData(1);
    
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  
  beforeDestroy() {
    // 清理资源
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }
};
</script>

<style scoped>
.dashboard-title-block {
  margin: 20px 0 20px;
  padding: 30px;
  overflow-x: hidden;
}

  .dashboard-title {
    font-size: 28px;
    font-weight: bold;
    color: #2e3968;
    margin: 0;
    margin-bottom: 16px;
  }

.dashboard-container {
  padding: 30px;
  background: #f9f9f9;
  overflow-x: hidden;
}

.page-footer {
  padding: 20px 30px;
  text-align: left;
  font-size: 14px;
  color: #888;
  line-height: 1.6;
}

@media (max-width: 1200px) {
  .dashboard-content {
    width: 85%;
  }
}

@media (max-width: 992px) {
  .dashboard-content {
    width: 90%;
  }
}

@media (max-width: 768px) {
  .dashboard-page {
    padding: 10px 0;
  }
  .dashboard-content {
    width: 95%;
  }
  .dashboard-title {
    font-size: 22px;
  }
  .dashboard-container {
    padding: 15px;
  }

  .page-footer {
    padding: 15px;
    font-size: 12px;
  }
}

</style>